import { useCallback, useContext, useMemo, useState } from 'react';
import { UserContext } from '../../context';
import { ProfileFeedCreateCardModal } from '../../pages';
import { Story, storyApi, StoryCard, storyCardApi } from '../../services';
import { CreateCardModal } from '../components';
import { getPostFieldsFromCard } from '../utils';

interface CardEditArgs {
  storyCard: StoryCard;
  isProfileFeedCard?: boolean;
}

export const useCardEdit = ({ storyCard, isProfileFeedCard }: CardEditArgs) => {
  const [storyCardById] = storyCardApi.endpoints.storyCardById.useLazyQuery();

  const [storiesByChannelId] = storyApi.endpoints.storiesByChannelId.useLazyQuery();

  const { channelId } = useContext(UserContext).userInfo.userData;

  const [card, setCard] = useState<StoryCard>(storyCard);

  const [postInStories, setPostInStories] = useState<Story[]>([]);

  const postFilledFields = useMemo(() => getPostFieldsFromCard(card), [card]);

  const [createCardModalOpen, setCreateCardModalOpen] = useState(false);

  const { id: cardId, storyId } = card;

  const getPostInStories = useCallback(async () => {
    if (isProfileFeedCard) {
      return [];
    }

    try {
      return await storiesByChannelId({ channelId, filter: { onlyPostIn: true } }).unwrap();
    } catch (_) {
      return [storyCard.story];
    }
  }, [channelId, isProfileFeedCard, storiesByChannelId, storyCard.story]);

  const cardEditHandler = useCallback(async () => {
    const [cardLatest, postInStoriesLatest] = await Promise.all([
      storyCardById({ storyId, cardId }).unwrap(),
      getPostInStories(),
    ]);

    setCard(cardLatest);
    setPostInStories(postInStoriesLatest);
    setCreateCardModalOpen(true);
  }, [cardId, getPostInStories, storyCardById, storyId]);

  const cardEditModal = useMemo(() => {
    if (!createCardModalOpen) {
      return null;
    }

    if (isProfileFeedCard) {
      return (
        <ProfileFeedCreateCardModal
          isOpen={createCardModalOpen}
          onClose={() => setCreateCardModalOpen(false)}
          card={card}
        />
      );
    }

    return (
      <CreateCardModal
        {...postFilledFields}
        isOpen={createCardModalOpen}
        onClose={() => setCreateCardModalOpen(false)}
        postInStories={postInStories}
        card={card}
      />
    );
  }, [card, createCardModalOpen, isProfileFeedCard, postFilledFields, postInStories]);

  return { cardEditModal, cardEditHandler };
};
